import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { TonConnectUIProvider, THEME, useTonWallet } from '@tonconnect/ui-react';
import './App.css';
import './i18n'; // Инициализация i18next
import Header from './components/Header/Header';
import Navigation from './components/Navigation/Navigation';
import FloatingGameButton from './components/FloatingGameButton/FloatingGameButton';
import BlockedScreen from './components/BlockedScreen';
import MaintenanceScreen from './components/MaintenanceScreen';
import TermsGate from './components/TermsGate';
import SubscriptionModal from './components/SubscriptionModal/SubscriptionModal';
import HomePage from './pages/HomePage';
import GamesPage from './pages/GamesPage';
import EarnPage from './pages/EarnPage';
import StakingPage from './pages/StakingPage';
import ReferralsPage from './pages/ReferralsPage';
import ProfilePage from './pages/ProfilePage';
import WalletPage from './pages/WalletPage';
import TasksPage from './pages/TasksPage';
import ReactoonsPage from './pages/ReactoonsPage';
// import MultiplayerPage from './pages/MultiplayerPage'; // TODO: страница не создана
import { useStore } from './store/useStore';
import wsService from './services/websocket';
import api, { gameAPI } from './services/api';
import SoundManager from './services/SoundManager';
import { BroadcastProvider } from './contexts/BroadcastContext';

// Обработка кнопки "Назад" в Telegram на Android
const TelegramBackButton: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    if (!tg?.BackButton) return;

    const isHome = location.pathname === '/';

    if (isHome) {
      tg.BackButton.hide();
    } else {
      tg.BackButton.show();
      const handleBack = () => navigate('/');
      tg.BackButton.onClick(handleBack);
      return () => {
        tg.BackButton.offClick(handleBack);
      };
    }
  }, [location.pathname, navigate]);

  return null;
};

// Автосинхронизация кошелька: при каждом открытии приложения
// если TonConnect уже подключён — отправляем адрес на бэкенд
const WalletSync: React.FC = () => {
  const wallet = useTonWallet();

  useEffect(() => {
    if (!wallet?.account?.address) return;

    const syncWallet = async () => {
      const token = localStorage.getItem('token');
      if (!token) return; // Авторизация ещё не прошла

      try {
        await api.post('/wallet/connect', {
          walletAddress: wallet.account.address
        });
      } catch (e) {
        // Тихая ошибка — не блокируем UI
        console.warn('Wallet sync failed:', e);
      }
    };

    // Небольшая задержка чтобы токен авторизации точно был готов
    const timer = setTimeout(syncWallet, 3000);
    return () => clearTimeout(timer);
  }, [wallet]);

  return null;
};

const App: React.FC = () => {
  const { initApp, graphicsQuality, language, updateBalance, user, musicEnabled, soundEnabled, selectedMusic, setUser, fetchPrices } = useStore();
  const [isLoading, setIsLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [isTelegramWebApp, setIsTelegramWebApp] = useState(true); // По умолчанию true, чтобы не мигала плашка
  const [showComingSoon, setShowComingSoon] = useState(false);

  // Состояние блокировки пользователя
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockData, setBlockData] = useState<{
    reason?: string;
    until?: string;
    permanent?: boolean;
  }>({});

  // Состояние режима обслуживания (maintenance mode)
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  // Состояние проверки подписки на каналы
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    channel: boolean;
    subscribed: boolean;
  } | null>(null);

  // 📜 Принята ли публичная оферта (null = ещё не знаем)
  const [termsAccepted, setTermsAccepted] = useState<boolean | null>(null);

  // Инициализация звуков при первом взаимодействии пользователя
  useEffect(() => {
    let initialized = false;

    const handleFirstInteraction = () => {
      if (initialized) return;
      initialized = true;

      console.log('🔊 [Android Fix v2] Инициализация звуков...');

      // ===== ANDROID FIX v2: AudioContext ПЕРВЫМ! =====
      // На Android нужно создать и resume() AudioContext синхронно в user gesture
      // ДО создания любых HTMLAudioElement
      console.log('🔊 [Android] Шаг 1: Разблокировка AudioContext...');
      SoundManager.resumeAudioContext();

      // Читаем настройки
      const actualSoundEnabled = soundEnabled !== false;
      const savedMusicEnabled = localStorage.getItem('musicEnabled');
      const actualMusicEnabled = savedMusicEnabled !== 'false';
      const savedSelectedMusic = localStorage.getItem('selectedMusic') || 'lunar_landing';

      // ===== Шаг 2: Музыка после разблокировки AudioContext =====
      if (actualMusicEnabled) {
        console.log('🎵 [Android] Шаг 2: Запуск музыки после AudioContext...');
        SoundManager.initBackgroundMusicDirect(savedSelectedMusic);
      }

      // Теперь инициализируем остальные звуки
      console.log('🔊 [Android] Шаг 3: Инициализация звуковых пулов...');
      SoundManager.init();
      SoundManager.setSoundEnabled(actualSoundEnabled);
      SoundManager.setMusicEnabled(actualMusicEnabled);

      // Разблокировка пулов звуков - асинхронно (для iOS и дополнительной совместимости)
      SoundManager.unlockAudioContext().then(() => {
        console.log('🔓 Пулы звуков разблокированы');
      });

      setTimeout(() => {
        document.removeEventListener('click', handleFirstInteraction, true);
        document.removeEventListener('touchstart', handleFirstInteraction, true);
        document.removeEventListener('touchend', handleFirstInteraction, true);
        document.removeEventListener('pointerdown', handleFirstInteraction, true);
        document.removeEventListener('keydown', handleFirstInteraction, true);
      }, 3000);
    };

    // НЕ вызываем автоматически - только по реальному user gesture!
    // setTimeout без user gesture не работает на Android
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      // НЕ используем setTimeout - это не user gesture!
      // НЕ используем события Telegram - они тоже не user gesture!
      console.log('🔊 [Android] Ждём реального user gesture для запуска аудио...');
    }

    // Вешаем обработчики с capture: true для раннего перехвата
    document.addEventListener('click', handleFirstInteraction, { capture: true, passive: true });
    document.addEventListener('touchstart', handleFirstInteraction, { capture: true, passive: true });
    document.addEventListener('touchend', handleFirstInteraction, { capture: true, passive: true });
    document.addEventListener('pointerdown', handleFirstInteraction, { capture: true, passive: true });
    document.addEventListener('keydown', handleFirstInteraction, { capture: true, passive: true });

    return () => {
      document.removeEventListener('click', handleFirstInteraction, true);
      document.removeEventListener('touchstart', handleFirstInteraction, true);
      document.removeEventListener('touchend', handleFirstInteraction, true);
      document.removeEventListener('pointerdown', handleFirstInteraction, true);
      document.removeEventListener('keydown', handleFirstInteraction, true);
    };
  }, [soundEnabled, musicEnabled]);

  // Синхронизация настроек при загрузке пользователя (без повторной инициализации!)
  // Основная инициализация музыки происходит в handleFirstInteraction
  useEffect(() => {
    if (user && !isLoading) {
      const savedMusicEnabled = localStorage.getItem('musicEnabled');
      const savedSelectedMusic = localStorage.getItem('selectedMusic') || 'lunar_landing';
      const actualMusicEnabled = savedMusicEnabled !== 'false';

      console.log('🎵 User loaded, syncing music settings (NOT reinitializing):', {
        savedSelectedMusic,
        actualMusicEnabled,
        soundEnabled
      });

      // Только синхронизируем настройки, НЕ вызываем unlockAudioContext повторно!
      // Это вызывает race condition на Android
      SoundManager.setMusicEnabled(actualMusicEnabled);
      SoundManager.setSoundEnabled(soundEnabled);
    }
  }, [user, isLoading]);

  // Синхронизация изменений настроек музыки и треков
  useEffect(() => {
    if (user && !isLoading) {
      console.log('🎵 Music settings changed, updating:', {
        musicEnabled,
        selectedMusic,
        soundEnabled
      });

      // Переключаем трек если изменился
      if (selectedMusic) {
        SoundManager.switchBackgroundMusic(selectedMusic);
      }

      // Обновляем состояние музыки
      SoundManager.setMusicEnabled(musicEnabled);
      SoundManager.setSoundEnabled(soundEnabled);
    }
  }, [musicEnabled, selectedMusic, soundEnabled, user, isLoading]);

  // МАКСИМАЛЬНО АГРЕССИВНАЯ принудительная стилизация TON Connect через глобальный CSS
  useEffect(() => {
    // Создаём <style> элемент с максимальным приоритетом
    const styleElement = document.createElement('style');
    styleElement.id = 'ton-connect-dark-theme-override';
    // БЕЗОПАСНО: статический CSS без пользовательских данных
    styleElement.innerHTML = `
      /* КРИТИЧЕСКИ ВАЖНО: Перекрываем ВСЕ белые фоны TON Connect */
      body *[role="menu"],
      body *[role="dialog"],
      body div[class*="tc"],
      body div[data-tonconnect],
      body div[data-tc-dropdown] {
        background: #16213e !important;
        background-color: #16213e !important;
        border: 1px solid rgba(123, 63, 242, 0.3) !important;
        border-radius: 12px !important;
        color: #ffffff !important;
      }

      /* Все дочерние элементы */
      body *[role="menu"] *,
      body *[role="dialog"] *,
      body div[class*="tc"] *,
      body div[data-tonconnect] *,
      body div[data-tc-dropdown] * {
        background: transparent !important;
        background-color: transparent !important;
        color: #ffffff !important;
      }

      /* Кнопки внутри */
      body *[role="menu"] button,
      body *[role="menu"] a,
      body *[role="dialog"] button,
      body *[role="dialog"] a,
      body div[class*="tc"] button,
      body div[class*="tc"] a {
        background: rgba(255, 255, 255, 0.05) !important;
        background-color: rgba(255, 255, 255, 0.05) !important;
        border: 1px solid rgba(123, 63, 242, 0.2) !important;
        border-radius: 8px !important;
        color: #ffffff !important;
        padding: 8px 12px !important;
      }

      body *[role="menu"] button:hover,
      body *[role="menu"] a:hover,
      body *[role="dialog"] button:hover,
      body *[role="dialog"] a:hover {
        background: rgba(123, 63, 242, 0.2) !important;
        background-color: rgba(123, 63, 242, 0.2) !important;
        border-color: rgba(123, 63, 242, 0.4) !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      // Удаляем при размонтировании
      const el = document.getElementById('ton-connect-dark-theme-override');
      if (el) el.remove();
    };
  }, []);

  // СУПЕР-АГРЕССИВНАЯ принудительная стилизация TON Connect dropdown
  useEffect(() => {
    const applyDarkThemeToElement = (element: HTMLElement) => {
      // Применяем темную тему к контейнеру МАКСИМАЛЬНО АГРЕССИВНО
      element.setAttribute('style', `
        background: #16213e !important;
        background-color: #16213e !important;
        border: 1px solid rgba(123, 63, 242, 0.3) !important;
        border-radius: 12px !important;
        color: #ffffff !important;
        ${element.getAttribute('style') || ''}
      `);

      // Применяем к всем дочерним элементам РЕКУРСИВНО
      const applyToChildren = (parent: HTMLElement) => {
        const children = parent.querySelectorAll('*');
        children.forEach((child) => {
          const htmlChild = child as HTMLElement;

          // Убираем белый фон везде
          const currentStyle = htmlChild.getAttribute('style') || '';
          const newStyle = currentStyle
            .replace(/background:\s*rgb\(255,\s*255,\s*255\)/gi, 'background: #16213e')
            .replace(/background-color:\s*rgb\(255,\s*255,\s*255\)/gi, 'background-color: #16213e')
            .replace(/background:\s*white/gi, 'background: #16213e')
            .replace(/background-color:\s*white/gi, 'background-color: #16213e')
            .replace(/background:\s*#fff/gi, 'background: #16213e')
            .replace(/background-color:\s*#fff/gi, 'background-color: #16213e');

          htmlChild.setAttribute('style', newStyle + '; color: #ffffff !important;');

          // Кнопки и ссылки
          if (htmlChild.tagName === 'BUTTON' || htmlChild.tagName === 'A') {
            htmlChild.setAttribute('style', `
              background: rgba(255, 255, 255, 0.05) !important;
              background-color: rgba(255, 255, 255, 0.05) !important;
              border: 1px solid rgba(123, 63, 242, 0.2) !important;
              border-radius: 8px !important;
              color: #ffffff !important;
              padding: 8px 12px !important;
              ${newStyle}
            `);
          }
        });
      };

      applyToChildren(element);
    };

    // Функция для поиска и стилизации ВСЕХ возможных dropdown
    const findAndStyleDropdowns = () => {
      // Ищем только конкретные селекторы - НЕ все элементы
      const selectors = [
        '[role="menu"]',
        '[role="dialog"]',
        'div[class*="tc"]',
        'div[class*="dropdown"]',
        'div[data-tc-dropdown]',
        'div[data-tonconnect]'
      ];

      selectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element) => {
            const htmlElement = element as HTMLElement;

            // Проверяем, что это НЕ settings-dropdown
            if (!htmlElement.classList.contains('settings-dropdown') &&
                !htmlElement.closest('.settings-dropdown')) {

              const computedStyle = window.getComputedStyle(htmlElement);
              const bg = computedStyle.backgroundColor;

              // ЛОГИРОВАНИЕ: Если фон белый - выводим информацию о элементе
              if (bg.includes('255, 255, 255') || bg.includes('rgb(255, 255, 255)')) {
                console.log('🔍 WHITE ELEMENT DETECTED:', {
                  selector: selector,
                  tagName: htmlElement.tagName,
                  classList: Array.from(htmlElement.classList),
                  id: htmlElement.id,
                  role: htmlElement.getAttribute('role'),
                  dataAttributes: Array.from(htmlElement.attributes)
                    .filter(attr => attr.name.startsWith('data-'))
                    .map(attr => `${attr.name}="${attr.value}"`),
                  backgroundColor: bg
                });

                // Применяем темную тему
                applyDarkThemeToElement(htmlElement);
              }
            }
          });
        } catch (e) {
          // Игнорируем ошибки селекторов
        }
      });
    };

    // MutationObserver для отслеживания появления dropdown
    const observer = new MutationObserver(() => {
      findAndStyleDropdowns();
    });

    // Наблюдаем за изменениями в body
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Проверяем существующие элементы при загрузке
    findAndStyleDropdowns();

    // КРИТИЧЕСКИ ВАЖНО: Постоянная проверка каждые 50ms (чаще)
    const intervalId = setInterval(() => {
      findAndStyleDropdowns();
    }, 50);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    // Проверяем блокировку при загрузке - ВСЕГДА запрашиваем актуальный статус с сервера
    const checkBlockStatus = async () => {
      const blockedData = localStorage.getItem('user_blocked');
      if (blockedData) {
        try {
          // Проверяем актуальный статус на сервере
          const response = await api.get('/user/profile');

          if (response.data?.is_blocked) {
            // Пользователь действительно заблокирован на сервере
            const data = JSON.parse(blockedData);
            setIsBlocked(true);
            setBlockData(data);
          } else {
            // Пользователь разблокирован - очищаем localStorage
            localStorage.removeItem('user_blocked');
            setIsBlocked(false);
            setBlockData({});
          }
        } catch (e) {
          console.error('Ошибка проверки статуса блокировки:', e);
          // Если ошибка запроса - используем локальные данные
          const data = JSON.parse(blockedData);
          setIsBlocked(true);
          setBlockData(data);
        }
      }
    };

    checkBlockStatus();

    // Слушаем событие блокировки
    const handleUserBlocked = (event: CustomEvent) => {
      setIsBlocked(true);
      setBlockData(event.detail);
    };

    window.addEventListener('user-blocked', handleUserBlocked as EventListener);

    return () => {
      window.removeEventListener('user-blocked', handleUserBlocked as EventListener);
    };
  }, []);

  // 📜 Публичная оферта: спрашиваем согласие один раз, после авторизации.
  // Статус общий с лобби слотов (одна таблица users), поэтому принявшим
  // в слотах экран здесь уже не покажется.
  useEffect(() => {
    if (isLoading) return;
    if (!localStorage.getItem('token')) return;

    let mounted = true;
    api.get('/user/terms')
      .then(res => {
        if (mounted) setTermsAccepted(!!res.data?.accepted);
      })
      .catch(() => {
        // Сбой сети — вход не блокируем, спросим в следующий раз
      });

    return () => { mounted = false; };
  }, [isLoading]);

  // Проверка режима обслуживания (maintenance mode)
  useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const response = await api.get('/auth/status');
        if (response.data?.maintenance) {
          setIsMaintenance(true);
          setMaintenanceMessage(response.data.message || '');
        } else {
          setIsMaintenance(false);
        }
      } catch (error) {
        console.error('Ошибка проверки статуса системы:', error);
        // При ошибке не блокируем пользователей
        setIsMaintenance(false);
      }
    };

    // Проверяем при загрузке
    checkMaintenanceStatus();

    // Периодически проверяем каждые 30 секунд
    const maintenanceInterval = setInterval(checkMaintenanceStatus, 30000);

    return () => {
      clearInterval(maintenanceInterval);
    };
  }, []);

  // Проверка подписки на обязательные каналы
  useEffect(() => {
    const checkSubscription = async () => {
      // Проверяем только если пользователь авторизован
      if (!user || isLoading) return;

      // Веб-версия: полностью пропускаем проверку подписки
      if (localStorage.getItem('webauth') === 'true') return;

      try {
        const response = await gameAPI.checkSubscription();
        const status = response.data.data;

        setSubscriptionStatus(status);

        if (!status.subscribed) {
          console.log('❌ Пользователь не подписан на каналы:', status);
          setShowSubscriptionModal(true);
        } else {
          console.log('✅ Пользователь подписан на все каналы');
          setShowSubscriptionModal(false);
        }
      } catch (error) {
        console.error('Ошибка проверки подписки:', error);
        // При ошибке не блокируем - пропускаем
      }
    };

    // Проверяем при загрузке
    checkSubscription();

    // Периодическая проверка каждые 5 минут
    const subscriptionInterval = setInterval(checkSubscription, 5 * 60 * 1000);

    return () => {
      clearInterval(subscriptionInterval);
    };
  }, [user, isLoading]);

  useEffect(() => {
    // Инициализация приложения
    const init = async () => {
      console.log('🚀 App initialization started');

      // 🔧 DEV MODE: Проверяем URL параметр devmode
      const urlParams = new URLSearchParams(window.location.search);
      const devMode = urlParams.get('devmode') === 'true';

      if (devMode) {
        console.log('🔧 DEV MODE АКТИВИРОВАН: обход Telegram проверки');

        // Создаём фейковый Telegram объект для dev режима
        if (!window.Telegram) {
          (window as any).Telegram = {};
        }

        // Проверяем параметр telegram_id из URL, если нет - используем дефолтного админа
        const telegramIdParam = urlParams.get('telegram_id');
        const USER_ID = telegramIdParam ? parseInt(telegramIdParam) : 592457038;

        // ВАЖНО: ПЕРЕЗАПИСЫВАЕМ Telegram.WebApp даже если он уже существует
        const initDataStr = `user={"id":${USER_ID},"first_name":"DevMode","last_name":"User","username":"devmode_user","language_code":"ru"}`;

        // Вибрация как fallback для HapticFeedback
        const vibrate = (ms: number) => { try { navigator.vibrate?.(ms); } catch {} };

        (window.Telegram as any).WebApp = {
          initDataUnsafe: {
            user: {
              id: USER_ID,
              first_name: 'DevMode',
              last_name: 'User',
              username: 'devmode_user',
              language_code: 'ru'
            }
          },
          initData: initDataStr,
          ready: () => console.log('🔧 Telegram WebApp ready (dev mode)'),
          expand: () => console.log('🔧 Telegram WebApp expand (dev mode)'),
          setHeaderColor: () => {},
          setBackgroundColor: () => {},
          close: () => {},
          isExpanded: true,
          viewportHeight: window.innerHeight,
          viewportStableHeight: window.innerHeight,
          MainButton: {
            text: '', color: '#000', textColor: '#fff', isVisible: false, isActive: true, isProgressVisible: false,
            show() { this.isVisible = true; },
            hide() { this.isVisible = false; },
            enable() { this.isActive = true; },
            disable() { this.isActive = false; },
            setText(t: string) { this.text = t; },
            showProgress(_leaveActive?: boolean) { this.isProgressVisible = true; },
            hideProgress() { this.isProgressVisible = false; },
            onClick(_cb: () => void) {}
          },
          BackButton: { isVisible: false, show() { this.isVisible = true; }, hide() { this.isVisible = false; }, onClick() {}, offClick() {} },
          HapticFeedback: {
            impactOccurred: (style: string) => { const ms = style === 'heavy' ? 50 : style === 'medium' ? 30 : 15; vibrate(ms); },
            notificationOccurred: (type: string) => { const ms = type === 'error' ? 80 : type === 'warning' ? 50 : 30; vibrate(ms); },
            selectionChanged: () => vibrate(10)
          },
          showAlert: (message: string, callback?: () => void) => { alert(message); callback?.(); },
          showConfirm: (message: string, callback?: (ok: boolean) => void) => { const ok = confirm(message); callback?.(ok); },
          showPopup: (params: any, callback?: (id: string) => void) => { alert(params.message || ''); callback?.('ok'); },
          openLink: (url: string) => window.open(url, '_blank'),
          openTelegramLink: (url: string) => window.open(url, '_blank'),
          shareToStory: () => { console.log('[DEV] shareToStory недоступен вне Telegram'); },
          safeAreaInset: { top: 0, bottom: 0, left: 0, right: 0 },
          contentSafeAreaInset: { top: 0, bottom: 0, left: 0, right: 0 }
        };

        // Сохраняем в localStorage для авторизации
        localStorage.setItem('telegram_user_id', USER_ID.toString());
        localStorage.setItem('telegram_init_data', initDataStr);

        console.log(`🔧 Dev Mode: залогинен как пользователь (ID: ${USER_ID})`);
        console.log(`🔧 localStorage установлен:`, {
          telegram_user_id: localStorage.getItem('telegram_user_id'),
          telegram_init_data: localStorage.getItem('telegram_init_data')
        });
      }

      // WEB AUTH: ПЕРВЫМ ДЕЛОМ проверяем web-mode (до Telegram SDK!)
      let webVerifyData: any = null; // Результат verify — переиспользуется ниже
      const isWebAuth = urlParams.get('webauth') === 'true' || localStorage.getItem('webauth') === 'true';
      if (isWebAuth && localStorage.getItem('token')) {
        console.log('🌐 WEB AUTH MODE: авторизация через email/web');
        localStorage.setItem('webauth', 'true');
        setIsTelegramWebApp(true); // Притворяемся ТГ чтобы приложение работало

        // Создаём минимальный mock Telegram WebApp для совместимости
        if (!window.Telegram) { (window as any).Telegram = {}; }
        if (!window.Telegram.WebApp) {
          // Вибрация как fallback для HapticFeedback в web
          const vibrate = (ms: number) => { try { navigator.vibrate?.(ms); } catch {} };

          (window.Telegram as any).WebApp = {
            initDataUnsafe: { user: { id: 0, first_name: 'Web', username: 'web_user', language_code: 'ru' } },
            initData: '',
            ready: () => {},
            expand: () => {},
            setHeaderColor: () => {},
            setBackgroundColor: () => {},
            close: () => {},
            isExpanded: true,
            viewportHeight: window.innerHeight,
            viewportStableHeight: window.innerHeight,
            // MainButton — полный мок
            MainButton: {
              text: '', color: '#000', textColor: '#fff', isVisible: false, isActive: true, isProgressVisible: false,
              show() { this.isVisible = true; },
              hide() { this.isVisible = false; },
              enable() { this.isActive = true; },
              disable() { this.isActive = false; },
              setText(t: string) { this.text = t; },
              showProgress(_leaveActive?: boolean) { this.isProgressVisible = true; },
              hideProgress() { this.isProgressVisible = false; },
              onClick(_cb: () => void) {}
            },
            BackButton: { isVisible: false, show() { this.isVisible = true; }, hide() { this.isVisible = false; }, onClick() {}, offClick() {} },
            // HapticFeedback — navigator.vibrate() как fallback
            HapticFeedback: {
              impactOccurred: (style: string) => { const ms = style === 'heavy' ? 50 : style === 'medium' ? 30 : 15; vibrate(ms); },
              notificationOccurred: (type: string) => { const ms = type === 'error' ? 80 : type === 'warning' ? 50 : 30; vibrate(ms); },
              selectionChanged: () => vibrate(10)
            },
            // Диалоги — нативные fallback
            showAlert: (message: string, callback?: () => void) => { alert(message); callback?.(); },
            showConfirm: (message: string, callback?: (ok: boolean) => void) => { const ok = confirm(message); callback?.(ok); },
            showPopup: (params: any, callback?: (id: string) => void) => { alert(params.message || ''); callback?.('ok'); },
            // Ссылки
            openLink: (url: string) => window.open(url, '_blank'),
            openTelegramLink: (url: string) => window.open(url, '_blank'),
            // Stories — в web недоступно, тихо пропускаем
            shareToStory: () => { console.log('[WEB] shareToStory недоступен вне Telegram'); },
            safeAreaInset: { top: 0, bottom: 0, left: 0, right: 0 },
            contentSafeAreaInset: { top: 0, bottom: 0, left: 0, right: 0 }
          };
        }

        // Верифицируем токен и загружаем данные юзера (один раз, результат переиспользуем)
        try {
          const token = localStorage.getItem('token');
          const verifyResp = await fetch('/api/auth/verify', { headers: { 'Authorization': `Bearer ${token}` } });
          webVerifyData = await verifyResp.json();
          if (webVerifyData.success && webVerifyData.user) {
            // Обновляем mock user данными из БД
            (window.Telegram as any).WebApp.initDataUnsafe.user = {
              id: webVerifyData.user.telegramId || 0,
              first_name: webVerifyData.user.firstName || 'Web',
              last_name: webVerifyData.user.lastName || '',
              username: webVerifyData.user.username || 'web_user',
              language_code: webVerifyData.user.languageCode || 'ru'
            };
            console.log('🌐 Web user verified:', webVerifyData.user.username);
          } else {
            // Токен невалидный — отправляем на логин
            localStorage.removeItem('token');
            localStorage.removeItem('webauth');
            window.location.href = '/login.html';
            return;
          }
        } catch (e) {
          console.error('🌐 Web auth verify failed:', e);
          localStorage.removeItem('token');
          localStorage.removeItem('webauth');
          window.location.href = '/login.html';
          return;
        }
      }

      // Проверяем, запущено ли приложение в Telegram (или активирован dev mode)
      const isInTelegram = devMode || (!!window.Telegram?.WebApp?.initData && window.Telegram.WebApp.initData !== '');
      setIsTelegramWebApp(isInTelegram || isWebAuth);

      // Если не в Telegram и не dev mode и не web auth - редирект на логин
      if (!isInTelegram && !isWebAuth) {
        console.log('❌ Not in Telegram, redirecting to login');
        const refParam = urlParams.get('ref');
        window.location.href = '/login.html' + (refParam ? '?ref=' + encodeURIComponent(refParam) : '');
        setIsLoading(false);
        return;
      }

      try {
        // В web-mode: переиспользуем данные из первого verify (без дублирования)
        if (isWebAuth && localStorage.getItem('token') && webVerifyData?.success && webVerifyData?.user) {
          console.log('🌐 Web mode: setting user from cached verify data');
          const u = webVerifyData.user;
          localStorage.setItem('user', JSON.stringify(u));
          const webUser = {
            id: u.id,
            telegramId: u.telegramId,
            username: u.username || 'Player',
            balance: parseFloat(u.balance) || 0,
            totalEarned: parseFloat(u.totalEarned) || 0,
            rank: u.rank || 'No Rank',
            vipLevel: u.vipLevel || 0,
            lastClaim: u.lastClaim ? new Date(u.lastClaim) : null,
            dailyEarnings: parseFloat(u.dailyEarnings) || 0,
            referralCount: u.referralCount || 0,
            referralBonus: u.referralBonus || 0,
            tasksDone: u.tasksDone || 0,
            miningRate: parseFloat(u.miningRate) || 83.3,
            totalAPR: parseFloat(u.totalAPR) || 0,
            vipBonusApr: parseFloat(u.vipBonusApr) || 0,
            firstName: u.firstName || u.username || 'Player',
            lastName: u.lastName || '',
            referralCode: u.referralCode || u.referral_code,
            photoUrl: u.photoUrl || u.photo_url || localStorage.getItem('user_avatar') || ''
          };
          setUser(webUser);
          // Обновляем кеш аватарки серверной (приоритет ТГ фото)
          if (u.photoUrl || u.photo_url) {
            localStorage.setItem('user_avatar', u.photoUrl || u.photo_url);
          }
          console.log('🌐 Web user loaded:', webUser.username, 'ID:', webUser.id);

          // Показываем контент СРАЗУ — остальное грузим в фоне
          setIsLoading(false);

          // Фоновые задачи: курсы, WebSocket, настройки
          fetchPrices().catch(console.error);
          const token = localStorage.getItem('token');
          if (token) wsService.connect(token);
          const savedQuality = localStorage.getItem('graphicsQuality') || 'high';
          document.querySelector('.app')?.setAttribute('data-quality', savedQuality);
          return; // Выходим — не нужен Telegram setup для веба
        } else if (isWebAuth) {
          // Токен не прошёл — редирект
          localStorage.removeItem('token');
          localStorage.removeItem('webauth');
          window.location.href = '/login.html';
          return;
        } else {
          console.log('Calling initApp...');
          await initApp();
        }

        // Подключаем WebSocket
        const token = localStorage.getItem('token');
        if (token) {
          wsService.connect(token);
        }

        // Применяем сохраненные настройки
        const savedQuality = localStorage.getItem('graphicsQuality') || 'high';
        document.querySelector('.app')?.setAttribute('data-quality', savedQuality);

        // Язык автоматически определяется i18next

        // Инициализация Telegram WebApp
        if (window.Telegram?.WebApp) {
          const tg = window.Telegram.WebApp as any;
          tg.ready();
          tg.expand();

          // Установка цветовой схемы
          try {
            tg.setHeaderColor('#1a1a2e');
            tg.setBackgroundColor('#1a1a2e');
          } catch (e) {
            console.log('Cannot set Telegram colors:', e);
          }

          // === SAFE AREA FIX ДЛЯ ANDROID ===
          // Устанавливаем CSS переменные для safe area (fallback если Telegram их не установил)
          const applySafeAreaInsets = () => {
            try {
              // Получаем safe area insets из Telegram API
              const contentSafeArea = tg.contentSafeAreaInset || {};
              const safeArea = tg.safeAreaInset || {};

              // Устанавливаем CSS переменные на :root
              const root = document.documentElement;

              // Content safe area (отступы от UI Telegram)
              if (contentSafeArea.bottom !== undefined) {
                root.style.setProperty('--tg-content-safe-area-inset-bottom', `${contentSafeArea.bottom}px`);
                console.log('📱 Telegram content safe area bottom:', contentSafeArea.bottom);
              }
              if (contentSafeArea.top !== undefined) {
                root.style.setProperty('--tg-content-safe-area-inset-top', `${contentSafeArea.top}px`);
              }

              // System safe area (notch, navigation bar)
              if (safeArea.bottom !== undefined) {
                root.style.setProperty('--tg-safe-area-inset-bottom', `${safeArea.bottom}px`);
                console.log('📱 Telegram system safe area bottom:', safeArea.bottom);
              }
              if (safeArea.top !== undefined) {
                root.style.setProperty('--tg-safe-area-inset-top', `${safeArea.top}px`);
              }

              // Логируем для отладки
              console.log('📱 Safe Area Insets applied:', {
                contentSafeArea,
                safeArea,
                viewportHeight: tg.viewportHeight,
                viewportStableHeight: tg.viewportStableHeight
              });
            } catch (e) {
              console.log('Cannot apply safe area insets:', e);
            }
          };

          // Применяем сразу
          applySafeAreaInsets();

          // Подписываемся на события изменения safe area (новые версии Telegram)
          if (tg.onEvent) {
            tg.onEvent('safeAreaChanged', applySafeAreaInsets);
            tg.onEvent('contentSafeAreaChanged', applySafeAreaInsets);
            tg.onEvent('viewportChanged', applySafeAreaInsets);
          }

          // Повторно применяем через 500ms (на случай если данные загрузились позже)
          setTimeout(applySafeAreaInsets, 500);
          setTimeout(applySafeAreaInsets, 1500);
          // === КОНЕЦ SAFE AREA FIX ===

          // Если есть данные пользователя, сохраняем их
          if (tg.initData && tg.initDataUnsafe?.user) {
            const telegramId = tg.initDataUnsafe.user.id;
            console.log('Telegram user detected:', telegramId);

            // Сохраняем в localStorage для использования на других страницах
            localStorage.setItem('telegram_user_id', telegramId.toString());
            localStorage.setItem('telegram_init_data', tg.initData);
          }
        }
      } catch (error) {
        console.error('❌ Ошибка инициализации:', error);
        console.error('Stack trace:', (error as any).stack);
      } finally {
        console.log('✅ Initialization complete, setting loading to false');
        setIsLoading(false);
      }
    };

    init();
  }, []);

  // === FIX: Восстановление Telegram viewport при переключении вкладок ===
  // Без этого при возврате на вкладку viewport может схлопнуться
  // и контент "исчезает", остаётся только фон
  useEffect(() => {
    const handleVisibilityRestore = () => {
      if (document.hidden) return;

      const tg = (window as any).Telegram?.WebApp;
      if (!tg) return;

      console.log('📱 Восстановление Telegram viewport после переключения вкладки');

      // Принудительно раскрываем WebApp
      try {
        tg.ready();
        tg.expand();
      } catch (e) {
        console.warn('Ошибка восстановления viewport:', e);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityRestore);
    window.addEventListener('focus', handleVisibilityRestore);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityRestore);
      window.removeEventListener('focus', handleVisibilityRestore);
    };
  }, []);

  useEffect(() => {

    // Подписываемся на WebSocket события
    const unsubscribeConnected = wsService.on('connected', (connected: boolean) => {
      setWsConnected(connected);
      console.log('WebSocket статус:', connected ? 'Подключен' : 'Отключен');
    });
    
    const unsubscribeBalance = wsService.on('balance_update', (data: any) => {
      if (data.newBalance !== undefined) {
        updateBalance(data.newBalance);
      }
    });
    
    const unsubscribeNotification = wsService.on('system_notification', (data: any) => {
      // Показываем системное уведомление
      console.log('Системное уведомление:', data.message);
      // Здесь можно добавить toast уведомление
    });
    
    // Очищаем подписки при размонтировании
    return () => {
      unsubscribeConnected();
      unsubscribeBalance();
      unsubscribeNotification();
      wsService.disconnect();
    };
  }, [initApp, updateBalance]);

  // Показываем экран обслуживания если режим maintenance активен
  // НО! Если devmode=true в URL - пропускаем (для админов)
  const urlParams = new URLSearchParams(window.location.search);
  const isDevMode = urlParams.get('devmode') === 'true';

  if (isMaintenance && !isDevMode) {
    return <MaintenanceScreen message={maintenanceMessage} />;
  }

  // Показываем экран блокировки если пользователь заблокирован
  if (isBlocked) {
    return (
      <BlockedScreen
        reason={blockData.reason}
        until={blockData.until}
        permanent={blockData.permanent}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loader">
          <div className="moon-loader"></div>
          <p>Loading TON MOON...</p>
        </div>
      </div>
    );
  }

  // Экран согласия с публичной офертой — до него в приложение не пускаем
  if (termsAccepted === false) {
    return <TermsGate onAccepted={() => setTermsAccepted(true)} />;
  }

  // УДАЛЕНО: Больше не показываем "In Development" экран
  // Приложение работает всегда, независимо от окружения

  const manifestUrl = process.env.NODE_ENV === 'production'
    ? 'https://moonraid.online/tonconnect-manifest.json'
    : 'http://localhost:3000/tonconnect-manifest.json';

  return (
    <TonConnectUIProvider
      manifestUrl={manifestUrl}
      uiPreferences={{
        theme: THEME.DARK,
        colorsSet: {
          [THEME.DARK]: {
            connectButton: {
              background: 'rgba(123, 63, 242, 0.2)',
              foreground: '#ffffff',
            },
            accent: '#7b3ff2',
            telegramButton: '#7b3ff2',
            background: {
              primary: '#16213e',
              secondary: '#1a1a2e',
              segment: '#16213e',
              tint: 'rgba(123, 63, 242, 0.1)',
              qr: '#16213e',
            },
            text: {
              primary: '#ffffff',
              secondary: 'rgba(255, 255, 255, 0.7)',
            },
            constant: {
              black: '#000000',
              white: '#ffffff',
            },
          },
        },
      }}
      actionsConfiguration={{
        twaReturnUrl: localStorage.getItem('webauth') === 'true' ? 'https://safepermoon.com' : 'https://t.me/moonraid_game_bot/moon'
      }}
    >
      <Router>
        <TelegramBackButton />
        <WalletSync />
        <BroadcastProvider>
        <div className="app" data-quality={graphicsQuality} data-lang={language} data-ws={wsConnected ? 'connected' : 'disconnected'}>
          {/* Индикатор WebSocket подключения */}
          {!wsConnected && (
            <div className="ws-indicator">
              <span className="ws-status offline">•</span>
              <span className="ws-text">Переподключение...</span>
            </div>
          )}

          {/* Верхняя панель с курсами и настройками */}
          <Header />
          
          {/* Основной контент */}
          <main className="app-main">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/games" element={<GamesPage />} />
              <Route path="/earn" element={<EarnPage />} />
              <Route path="/staking" element={<StakingPage />} />
              <Route path="/referrals" element={<ReferralsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/slot" element={<ReactoonsPage />} />
              {/* <Route path="/multiplayer" element={<MultiplayerPage />} /> */}
              <Route path="/index.html" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          
          {/* Нижняя навигация */}
          <Navigation />
          
          {/* Плавающая кнопка для игр */}
          <FloatingGameButton />

          {/* Модальное окно проверки подписки */}
          {showSubscriptionModal && subscriptionStatus && (
            <SubscriptionModal
              subscriptionStatus={subscriptionStatus}
              onSubscribed={() => {
                setShowSubscriptionModal(false);
                if (localStorage.getItem('webauth') === 'true') {
                  localStorage.setItem('web_sub_dismissed', 'true');
                }
                console.log('✅ Подписка закрыта');
              }}
            />
          )}
        </div>
        </BroadcastProvider>
      </Router>
    </TonConnectUIProvider>
  );
};

export default App;